/**
 * Dummy Data Generator for Graphs Development
 * Generates realistic track point data for testing and development
 */

import moment from 'moment';

// ============================================================================
// DUMMY DATA CONFIGURATION
// ============================================================================
// Set to true to enable dummy data for development/testing
// Set to false to disable dummy data (use real API data only)
//
// TO REMOVE DUMMY DATA BEFORE PRODUCTION:
// 1. Set ENABLE_DUMMY_DATA = false
// 2. Delete this file (dummyData.js)
// 3. Remove import from GraphsContainer.js
// 4. Remove dummy data logic from GraphsContainer.js (search for "dummy")
// ============================================================================
const ENABLE_DUMMY_DATA = false;
// ============================================================================

/**
 * Generate dummy track points for testing
 * @param {number} count - Number of track points to generate
 * @param {Object} timeRange - Time range for the data
 * @returns {Array} Array of track point objects
 */
export const generateDummyTrackPoints = (count = 50, timeRange = null) => {
  const trackPoints = [];

  // Default to last 24 hours if no time range provided
  const endTime = timeRange?.endDate ? moment(timeRange.endDate) : moment();
  const startTime = timeRange?.startDate ? moment(timeRange.startDate) : moment().subtract(24, 'hours');

  const timeStep = endTime.diff(startTime) / count;

  // Starting position (somewhere in Singapore)
  let lat = 1.3521 + (Math.random() - 0.5) * 0.1;
  let lng = 103.8198 + (Math.random() - 0.5) * 0.1;

  // Starting values
  let speed = 20 + Math.random() * 40; // 20-60 km/h
  let mileage = 50000 + Math.random() * 10000; // Starting mileage
  let fuel = 60 + Math.random() * 30; // 60-90%
  let externalVoltage = 12.5 + Math.random() * 1.5; // 12.5-14V
  let internalVoltage = 3.7 + Math.random() * 0.5; // 3.7-4.2V
  let ignition = true;

  for (let i = 0; i < count; i++) {
    const currentTime = startTime.clone().add(i * timeStep, 'milliseconds');

    // Simulate realistic movement
    const speedChange = (Math.random() - 0.5) * 10;
    speed = Math.max(0, Math.min(120, speed + speedChange));

    // Simulate position change based on speed
    const distanceKm = (speed / 3600) * (timeStep / 1000); // Distance in km
    const latChange = (distanceKm / 111) * (Math.random() - 0.5) * 2;
    const lngChange = (distanceKm / 111) * (Math.random() - 0.5) * 2;
    lat += latChange;
    lng += lngChange;

    // Update mileage
    mileage += distanceKm;

    // Simulate fuel consumption (decreases when moving)
    if (speed > 0) {
      fuel = Math.max(10, fuel - (Math.random() * 0.5));
    }

    // Simulate voltage fluctuations
    externalVoltage = Math.max(11.5, Math.min(14.5, externalVoltage + (Math.random() - 0.5) * 0.2));
    internalVoltage = Math.max(3.5, Math.min(4.2, internalVoltage + (Math.random() - 0.5) * 0.1));

    // Randomly toggle ignition (mostly on)
    if (Math.random() < 0.05) {
      ignition = !ignition;
      if (!ignition) {
        speed = 0;
      }
    }

    const trackPoint = {
      packet_id: `dummy-packet-${i}-${currentTime.valueOf()}`,
      source_id: 'DUMMY-DEVICE-001',
      plate_number: 'SGX1234A',
      source_time: currentTime.valueOf(),
      server_time: currentTime.valueOf() + Math.random() * 1000,
      gps: {
        position: {
          lat: parseFloat(lat.toFixed(6)),
          lng: parseFloat(lng.toFixed(6)),
          alt: parseFloat((50 + Math.random() * 100).toFixed(1)),
          is_valid: true,
        },
        hdop: parseFloat((0.5 + Math.random() * 2).toFixed(1)),
      },
      velocity: {
        speed: parseFloat((speed / 3.6).toFixed(2)), // Convert km/h to m/s
        direction: parseFloat((Math.random() * 360).toFixed(0)),
      },
      ignition: ignition,
      mileage: parseFloat((mileage * 1000).toFixed(0)), // Convert to meters
      fuel: parseFloat(fuel.toFixed(1)),
      voltage: {
        external: parseFloat(externalVoltage.toFixed(2)),
        internal: parseFloat(internalVoltage.toFixed(2)),
      },
    };

    trackPoints.push(trackPoint);
  }

  return trackPoints;
};

/**
 * Check if dummy data should be used
 * @returns {boolean}
 */
export const shouldUseDummyData = () => {
  // Return the configuration flag
  return ENABLE_DUMMY_DATA;
};

/**
 * Get dummy data info message
 * @returns {string}
 */
export const getDummyDataMessage = () => {
  return '📊 Viewing dummy data for development. Change ENABLE_DUMMY_DATA flag in dummyData.js to disable.';
};
